import React, { useEffect, useState } from "react";

export const usePlotly = (
  containerRef: React.RefObject<HTMLElement>,
  trigger: boolean
) => {
  const api = window.__rendering!.plotly!;

  // BLD2-609: mirror `api.ready` into React state. The singleton's `ready`
  // property is mutated outside React inside `api.load()`'s async resolve,
  // so without this, effects keyed on it never re-run when Plotly finishes
  // loading after mount.
  const [isPlotlyReady, setIsPlotlyReady] = useState<boolean>(api.ready);

  // Load library on mount.
  useEffect(() => {
    if (api.ready) {
      setIsPlotlyReady(true);
      return;
    }
    let cancelled = false;
    api.load().then(() => {
      if (!cancelled) setIsPlotlyReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-render when state or content changes.
  useEffect(() => {
    if (!isPlotlyReady || !containerRef.current || !trigger) return;
    api.renderAll(containerRef.current);
  }, [trigger, isPlotlyReady]);

  // Watch the container for new charts and render those.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isPlotlyReady) return;

    const tryRender = (el: HTMLElement) => {
      if (
        (el.classList.contains("plotly-graph") && el.dataset.graphInfo) ||
        el.classList.contains("graph-editor-placeholder")
      ) {
        setTimeout(() => {
          api.renderElement(el);
        }, 1000);
      }
    };

    const renderFromNode = (node: Node) => {
      if (!(node instanceof HTMLElement)) return;

      tryRender(node);

      // Check for base graphs.
      for (const child of node.querySelectorAll<HTMLElement>(".plotly-graph")) {
        tryRender(child);
      }

      // Check for ediable placeholder graphs.
      for (const child of node.querySelectorAll<HTMLElement>(
        ".graph-editor-placeholder"
      )) {
        tryRender(child);
      }
    };

    // BLD2-609: process anything ALREADY in the container before observing.
    // The MutationObserver only sees future mutations, so placeholders
    // injected before Plotly finished loading would otherwise be missed.
    renderFromNode(container);

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          renderFromNode(node);
        }
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isPlotlyReady]);

  // Handle container resizing.
  useEffect(() => {
    if (!isPlotlyReady || !containerRef.current) return;

    const ro = new ResizeObserver(() => {
      if (!containerRef.current) return;
      const charts = containerRef.current.querySelectorAll(".plotly-graph");
      for (const chart of charts) {
        api.Plotly?.Plots.resize(chart as HTMLElement);
      }
    });

    ro.observe(containerRef.current);

    return () => ro.disconnect();
  }, [isPlotlyReady]);
};
